import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, LOGO_URL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (redirect) navigate(redirect);
      else navigate(user.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Ciara's Kidz" className="h-20 w-auto mx-auto object-contain" />
          <h1 className="font-display text-2xl font-bold text-purple-950 mt-4">Welcome back</h1>
          <p className="text-muted-foreground text-sm mt-1">Log in to view your orders and history.</p>
        </div>
        <form onSubmit={submit} data-testid="login-form" className="bg-white rounded-3xl border border-purple-100 p-8 shadow-xl shadow-purple-100/50 space-y-4">
          {error && (
            <div data-testid="login-error" className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
              {error}
            </div>
          )}
          <div>
            <Label>Email</Label>
            <Input
              data-testid="client-login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1.5 bg-white"
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              data-testid="client-login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 bg-white"
            />
          </div>
          <button
            type="submit"
            data-testid="client-login-submit-button"
            disabled={loading}
            className="w-full py-3.5 rounded-full brand-gradient text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
          >
            {loading ? "Logging in..." : "Log In"}
          </button>
          <p className="text-center text-sm text-muted-foreground">
            New here?{" "}
            <Link to="/register" className="text-fuchsia-600 font-semibold hover:underline">
              Create an account
            </Link>
          </p>
        </form>
        <p className="text-center text-xs text-muted-foreground mt-4">
          Admin? Log in with your admin credentials to access the dashboard.
        </p>
      </div>
    </div>
  );
}
